import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import SecurityIcon from "@material-ui/icons/Security";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import MaterialTable, { Column } from "material-table";
import BreadcrumbNav from "../../../../components/BreadcrumbNav/BreadcrumbNav";
import CustomButton from "../../../../components/Button/CustomButton";
import DeleteConfirmationDialog from "../../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { materialTableES } from "../../../../services/materialTableES";
import { errorToast, successToast } from "../../../../services/toasts";
import { RbacMember, RbacRole } from "../../../../types/rbac";
import styles from "./EmployeesManagement.module.scss";
import {
  useCreateRbacMember,
  useDeleteRbacMember,
  useGetRbacMembers,
  useGetRbacRoles,
  useSetRbacMemberRoles,
  useToggleRbacMemberActive,
} from "../../../../utils/react-query/queryRbac/queries";

type EmployeeFormState = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  cedula: string;
  password: string;
  role_ids: number[];
};

const INITIAL_FORM: EmployeeFormState = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  cedula: "",
  password: "",
  role_ids: [],
};

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  return error instanceof Error ? error.message : fallbackMessage;
};

const EmployeesManagement = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRolesDialogOpen, setIsRolesDialogOpen] = useState(false);
  const [employeeForm, setEmployeeForm] =
    useState<EmployeeFormState>(INITIAL_FORM);
  const [selectedMember, setSelectedMember] = useState<RbacMember | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const { data: members = [], isLoading: isLoadingMembers } =
    useGetRbacMembers();
  const { data: roles = [], isLoading: isLoadingRoles } = useGetRbacRoles();
  const { mutateAsync: createMember, isLoading: isCreatingMember } =
    useCreateRbacMember();
  const { mutateAsync: toggleMemberActive } = useToggleRbacMemberActive();
  const { mutateAsync: deleteMember } = useDeleteRbacMember();
  const { mutateAsync: setMemberRoles, isLoading: isSavingRoles } =
    useSetRbacMemberRoles();

  const summary = useMemo(() => {
    const activeCount = members.filter((member) => member.is_active).length;

    return {
      totalMembers: members.length,
      activeMembers: activeCount,
      totalRoles: roles.length,
    };
  }, [members, roles]);

  const handleChange =
    (field: keyof Omit<EmployeeFormState, "role_ids">) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmployeeForm((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const toggleRoleInCreateForm = (roleId: number) => {
    setEmployeeForm((prev) => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter((currentRoleId) => currentRoleId !== roleId)
        : [...prev.role_ids, roleId],
    }));
  };

  const toggleAssignedRole = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((currentRoleId) => currentRoleId !== roleId)
        : [...prev, roleId]
    );
  };

  const closeCreateDialog = () => {
    setEmployeeForm(INITIAL_FORM);
    setIsCreateDialogOpen(false);
  };

  const handleCreateEmployee = async () => {
    const requiredFields = [
      employeeForm.first_name,
      employeeForm.last_name,
      employeeForm.email,
      employeeForm.phone,
      employeeForm.password,
    ];

    if (requiredFields.some((field) => !field.trim())) {
      return errorToast("Completa todos los campos obligatorios del empleado");
    }

    if (employeeForm.role_ids.length === 0) {
      return errorToast("Selecciona al menos un rol para el empleado");
    }

    try {
      await createMember({
        ...employeeForm,
        cedula: employeeForm.cedula.trim() || undefined,
      });

      successToast("Empleado creado con éxito");
      closeCreateDialog();
    } catch (error) {
      errorToast(getErrorMessage(error, "No fue posible crear el empleado"));
    }
  };

  const handleToggleActive = async (member: RbacMember) => {
    try {
      await toggleMemberActive({
        memberId: member.id,
        is_active: !member.is_active,
      });

      successToast(
        `El empleado fue ${
          member.is_active ? "desactivado" : "activado"
        } con éxito`
      );
    } catch (error) {
      errorToast(
        getErrorMessage(
          error,
          "No fue posible actualizar el estado del empleado"
        )
      );
    }
  };

  const openRolesDialog = (member: RbacMember) => {
    setSelectedMember(member);
    setSelectedRoleIds(member.roles.map((role) => role.id));
    setIsRolesDialogOpen(true);
  };

  const closeRolesDialog = () => {
    setSelectedMember(null);
    setSelectedRoleIds([]);
    setIsRolesDialogOpen(false);
  };

  const handleSaveAssignedRoles = async () => {
    if (!selectedMember) {
      return;
    }

    try {
      await setMemberRoles({
        memberId: selectedMember.id,
        role_ids: selectedRoleIds,
      });

      successToast("Roles del empleado actualizados con éxito");
      closeRolesDialog();
    } catch (error) {
      errorToast(
        getErrorMessage(
          error,
          "No fue posible actualizar los roles del empleado"
        )
      );
    }
  };

  const handleDeleteMember = async (memberId: number) => {
    try {
      await deleteMember(memberId);
      successToast("Empleado eliminado con éxito");
    } catch (error) {
      errorToast(getErrorMessage(error, "No fue posible eliminar el empleado"));
    }
  };

  const renderRoles = (memberRoles: RbacRole[]) => {
    if (!memberRoles.length) {
      return (
        <Typography className={styles.secondaryText}>Sin roles</Typography>
      );
    }

    return (
      <div className={styles.rolesContainer}>
        {memberRoles.map((role) => (
          <Chip
            key={role.id}
            label={role.name}
            size="small"
            className={role.is_system ? styles.systemChip : styles.customChip}
          />
        ))}
      </div>
    );
  };

  const columns: Column<RbacMember>[] = [
    {
      title: "Empleado",
      field: "user.first_name",
      render: (rowData) => (
        <div>
          <Typography className={styles.nameText}>
            {`${rowData.user?.first_name || ""} ${
              rowData.user?.last_name || ""
            }`}
          </Typography>
          <Typography className={styles.secondaryText}>
            {rowData.user?.cedula || "Sin cédula"}
          </Typography>
        </div>
      ),
    },
    {
      title: "Correo",
      field: "user.email",
      render: (rowData) => rowData.user?.email || "-",
    },
    {
      title: "Teléfono",
      field: "user.phone",
      render: (rowData) => rowData.user?.phone || "-",
    },
    {
      title: "Roles",
      field: "roles",
      sorting: false,
      render: (rowData) => renderRoles(rowData.roles || []),
    },
    {
      title: "Activo",
      field: "is_active",
      filtering: false,
      render: (rowData) => (
        <Switch
          color="primary"
          checked={rowData.is_active}
          onChange={() => handleToggleActive(rowData)}
        />
      ),
    },
    {
      title: "Acciones",
      field: "actions",
      sorting: false,
      filtering: false,
      render: (rowData) => (
        <Box display="flex" alignItems="center">
          <Tooltip title="Asignar roles">
            <IconButton onClick={() => openRolesDialog(rowData)}>
              <SecurityIcon className={styles.assignRoleIcon} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar empleado">
            <IconButton
              onClick={() =>
                setConfirmDialog({
                  isOpen: true,
                  title: "Eliminar empleado",
                  subTitle:
                    "Esta acción retirará al empleado del proveedor. ¿Deseas continuar?",
                  onConfirm: async () => {
                    await handleDeleteMember(rowData.id);
                    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
                  },
                })
              }
            >
              <DeleteIcon className={styles.deleteIcon} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <section className="layout-dashboard-container">
      <div className="header-container">
        <Box className={styles.headerRow}>
          <div>
            <h4 className="header-title">Empleados</h4>
            <BreadcrumbNav
              firstText="Proveedor"
              secondText="Empleados"
              secondLink="/proveedor/empleados"
              thirdText="Gestión"
            />
          </div>
        </Box>
      </div>

      <DeleteConfirmationDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />

      <div className={styles.actionBar}>
        <Grid container spacing={2}>
          {[
            { label: "Total empleados", value: summary.totalMembers },
            { label: "Activos", value: summary.activeMembers },
            { label: "Roles disponibles", value: summary.totalRoles },
          ].map((item) => (
            <Grid item xs={12} sm={4} key={item.label}>
              <Paper className={styles.summaryCard} elevation={0}>
                <Typography className={styles.summaryLabel}>
                  {item.label}
                </Typography>
                <Typography className={styles.summaryValue}>
                  {item.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </div>

      <Paper className={styles.infoCard} elevation={0}>
        <Typography className={styles.nameText}>
          Crea empleados internos del proveedor y asígnales uno o varios roles.
        </Typography>
        <Typography className={styles.secondaryText}>
          Estos usuarios corresponden al nuevo flujo RBAC del tenant, distinto
          al salesperson público.
        </Typography>
      </Paper>

      <Box display="flex" justifyContent="flex-end" marginBottom={3}>
        <CustomButton
          color="primary"
          shape="little-rounded"
          icon={PersonAddIcon}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          Crear empleado
        </CustomButton>
      </Box>

      <MaterialTable
        title=""
        columns={columns}
        data={members}
        isLoading={isLoadingMembers || isLoadingRoles}
        localization={materialTableES}
        options={{
          search: true,
          filtering: false,
          actionsColumnIndex: -1,
          headerStyle: {
            backgroundColor: "#f4f6f8",
            color: "#637381",
            fontSize: "14px",
            fontWeight: 600,
          },
          rowStyle: {
            fontSize: "14px",
            color: "#1C252E",
          },
          searchFieldVariant: "outlined",
        }}
      />

      <Dialog
        open={isCreateDialogOpen}
        onClose={closeCreateDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Crear empleado</DialogTitle>
        <DialogContent dividers>
          <Paper className={styles.infoCard} elevation={0}>
            <Typography className={styles.nameText}>
              Guarda la contraseña en un lugar seguro.
            </Typography>
            <Typography className={styles.secondaryText}>
              Esta es la credencial inicial del empleado para el acceso interno
              del proveedor.
            </Typography>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre"
                variant="outlined"
                value={employeeForm.first_name}
                onChange={handleChange("first_name")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Apellido"
                variant="outlined"
                value={employeeForm.last_name}
                onChange={handleChange("last_name")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Correo"
                variant="outlined"
                value={employeeForm.email}
                onChange={handleChange("email")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Celular"
                variant="outlined"
                value={employeeForm.phone}
                onChange={handleChange("phone")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cédula"
                variant="outlined"
                value={employeeForm.cedula}
                onChange={handleChange("cedula")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="password"
                label="Contraseña"
                variant="outlined"
                value={employeeForm.password}
                onChange={handleChange("password")}
              />
            </Grid>
          </Grid>

          <Box marginTop={3}>
            <Typography className={styles.nameText}>
              Roles del empleado
            </Typography>
            <Typography className={styles.secondaryText}>
              Selecciona los permisos que heredará este usuario desde sus roles.
            </Typography>
          </Box>

          <Grid container spacing={2} className={styles.rolesGrid}>
            {roles.map((role) => (
              <Grid item xs={12} sm={6} key={role.id}>
                <Paper className={styles.roleSelector} elevation={0}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={employeeForm.role_ids.includes(role.id)}
                        onChange={() => toggleRoleInCreateForm(role.id)}
                      />
                    }
                    label={
                      <div>
                        <Typography className={styles.roleTitle}>
                          {role.name}
                        </Typography>
                        <Typography className={styles.roleDescription}>
                          {role.description || "Sin descripción"}
                        </Typography>
                      </div>
                    }
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="outlined"
            shape="little-rounded"
            onClick={closeCreateDialog}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            color="primary"
            shape="little-rounded"
            onClick={handleCreateEmployee}
            isLoading={isCreatingMember}
          >
            Crear empleado
          </CustomButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRolesDialogOpen}
        onClose={closeRolesDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedMember
            ? `Roles de ${selectedMember.user?.first_name || "empleado"}`
            : "Roles del empleado"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {roles.map((role) => (
              <Grid item xs={12} sm={6} key={role.id}>
                <Paper className={styles.roleSelector} elevation={0}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={selectedRoleIds.includes(role.id)}
                        onChange={() => toggleAssignedRole(role.id)}
                      />
                    }
                    label={
                      <div>
                        <Typography className={styles.roleTitle}>
                          {role.name}
                        </Typography>
                        <Typography className={styles.roleDescription}>
                          {role.description || "Sin descripción"}
                        </Typography>
                      </div>
                    }
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="outlined"
            shape="little-rounded"
            onClick={closeRolesDialog}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            color="primary"
            shape="little-rounded"
            onClick={handleSaveAssignedRoles}
            isLoading={isSavingRoles}
          >
            Guardar roles
          </CustomButton>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default EmployeesManagement;
